export const SUPER_ADMIN_AUTH_STORAGE_KEY = "superAdminAuth";

export interface SuperAdminAuthUserPayload {
  token: string;
  user: Record<string, unknown> | null;
}

export const getStoredSuperAdminAuth = (): SuperAdminAuthUserPayload | null => {
  const raw = sessionStorage.getItem(SUPER_ADMIN_AUTH_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as SuperAdminAuthUserPayload;
  } catch {
    return null;
  }
};

export const setStoredSuperAdminAuth = (
  auth: SuperAdminAuthUserPayload
): void => {
  sessionStorage.setItem(SUPER_ADMIN_AUTH_STORAGE_KEY, JSON.stringify(auth));
};

export const clearStoredSuperAdminAuth = (): void => {
  sessionStorage.removeItem(SUPER_ADMIN_AUTH_STORAGE_KEY);
};

const decodeJwtExp = (token: string): number | null => {
  try {
    const parts = token.split(".");

    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    ) as { exp?: number };

    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
};

/** True when sessionStorage holds a non-expired Super Admin JWT. */
export const isStoredSuperAdminSessionValid = (): boolean => {
  const auth = getStoredSuperAdminAuth();

  if (!auth?.token) {
    return false;
  }

  const exp = decodeJwtExp(auth.token);

  if (exp === null) {
    return true;
  }

  return Date.now() < exp * 1000;
};

/** Pure SA shell: on /super-admin/* (not login) with a valid SA token — no tenant redirect. */
export const isPureSuperAdminShellRoute = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const { pathname } = window.location;

  return (
    pathname.startsWith("/super-admin") && pathname !== "/super-admin/login"
  );
};
